import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { accessRequestService } from '../../../services/accessRequestService';

const EmergencyOverridePanel = ({ onOverride }) => {
  const [overrideType, setOverrideType] = useState('');
  const [targetEmail, setTargetEmail] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const overrideActions = [
    {
      id: 'force_approve',
      title: 'Force Approve Profile',
      description: 'Immediately approve a profile bypassing all verification checks',
      icon: 'ShieldCheck',
      color: 'success'
    },
    {
      id: 'force_reject',
      title: 'Force Reject Profile',
      description: 'Immediately reject a profile with override authority',
      icon: 'ShieldX',
      color: 'error'
    },
    {
      id: 'reset_status',
      title: 'Reset Profile Status',
      description: 'Reset profile to pending status for re-review',
      icon: 'RotateCcw',
      color: 'warning'
    },
    {
      id: 'activate_user',
      title: 'Emergency User Activation',
      description: 'Activate user account immediately for urgent access',
      icon: 'Zap',
      color: 'primary'
    }
  ];

  const handleOverrideAction = async () => {
    if (!targetEmail?.trim()) {
      alert('Please enter target email address');
      return;
    }
    if (!overrideReason?.trim()) {
      alert('Please provide override reason for audit trail');
      return;
    }

    setIsProcessing(true);
    try {
      // Get profile by email
      const profile = await accessRequestService?.getByEmail(targetEmail);
      if (!profile) {
        alert('Profile not found with email: ' + targetEmail);
        return;
      }

      // Execute override action
      switch (overrideType) {
        case 'force_approve':
          await accessRequestService?.update(profile?.id, {
            status: 'approved',
            adminNotes: `EMERGENCY OVERRIDE: ${overrideReason}`,
            reviewedAt: new Date()?.toISOString()
          });
          break;
        case 'force_reject':
          await accessRequestService?.update(profile?.id, {
            status: 'rejected',
            adminNotes: `EMERGENCY OVERRIDE: ${overrideReason}`,
            reviewedAt: new Date()?.toISOString()
          });
          break;
        case 'reset_status':
          await accessRequestService?.update(profile?.id, {
            status: 'pending',
            adminNotes: `STATUS RESET: ${overrideReason}`,
            reviewedAt: null
          });
          break;
        case 'activate_user':
          await accessRequestService?.update(profile?.id, {
            status: 'approved',
            adminNotes: `EMERGENCY ACTIVATION: ${overrideReason}`,
            reviewedAt: new Date()?.toISOString()
          });
          break;
      }

      onOverride(`${overrideType} for ${targetEmail}`);
      setShowConfirmModal(false);
      setTargetEmail('');
      setOverrideReason('');
      setOverrideType('');
      alert('Emergency override executed successfully');
    } catch (error) {
      console.error('Override error:', error);
      alert('Override failed: ' + error?.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Warning Banner */}
        <div className="bg-error/10 border-2 border-error rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-error flex items-center justify-center flex-shrink-0">
              <Icon name="AlertTriangle" size={24} color="#FFFFFF" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-error mb-2">Emergency Override Controls</h2>
              <p className="text-sm text-muted-foreground mb-4">
                These controls bypass all standard verification processes and directly modify database records.
                Use only in urgent situations requiring immediate administrative intervention.
              </p>
              <div className="flex items-center gap-2 text-xs text-error">
                <Icon name="Shield" size={14} />
                <span className="font-medium">All actions are permanently logged in audit trail</span>
              </div>
            </div>
          </div>
        </div>

        {/* Override Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {overrideActions?.map((action) => (
            <button
              key={action?.id}
              onClick={() => {
                setOverrideType(action?.id);
                setShowConfirmModal(true);
              }}
              className={`bg-card border-2 rounded-2xl p-6 text-left hover:border-${action?.color} transition-all group`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl bg-${action?.color}/10 flex items-center justify-center group-hover:bg-${action?.color}/20 transition-colors`}>
                  <Icon name={action?.icon} size={24} className={`text-${action?.color}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-1">{action?.title}</h3>
                  <p className="text-sm text-muted-foreground">{action?.description}</p>
                </div>
                <Icon name="ChevronRight" size={20} className="text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
            </button>
          ))}
        </div>

        {/* Quick Access Section */}
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">Quick Emergency Access</h3>
          <div className="space-y-4">
            <Input
              label="Target Email Address"
              type="email"
              placeholder="user@example.com"
              value={targetEmail}
              onChange={(e) => setTargetEmail(e?.target?.value)}
              iconName="Mail"
            />
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setOverrideType('force_approve');
                  setShowConfirmModal(true);
                }}
                disabled={!targetEmail}
                iconName="CheckCircle"
                className="flex-1 bg-success hover:bg-success/90 text-white"
              >
                Quick Approve
              </Button>
              <Button
                onClick={() => {
                  setOverrideType('activate_user');
                  setShowConfirmModal(true);
                }}
                disabled={!targetEmail}
                iconName="Zap"
                className="flex-1 bg-primary hover:bg-primary/90 text-white"
              >
                Emergency Activate
              </Button>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">System Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-success/10 rounded-xl">
              <div className="flex items-center gap-2">
                <Icon name="Database" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">Database Connection</span>
              </div>
              <span className="text-xs font-medium text-success">ACTIVE</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-success/10 rounded-xl">
              <div className="flex items-center gap-2">
                <Icon name="Shield" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">RLS Policies</span>
              </div>
              <span className="text-xs font-medium text-success">BYPASSED</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-success/10 rounded-xl">
              <div className="flex items-center gap-2">
                <Icon name="FileText" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">Audit Logging</span>
              </div>
              <span className="text-xs font-medium text-success">ENABLED</span>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 max-w-md w-full elevation-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Confirm Emergency Override</h3>
                <p className="text-sm text-muted-foreground">
                  {overrideActions?.find(a => a?.id === overrideType)?.title}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <Input
                label="Target Email"
                type="email"
                value={targetEmail}
                onChange={(e) => setTargetEmail(e?.target?.value)}
                placeholder="user@example.com"
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Override Reason (Required for Audit)
                </label>
                <textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e?.target?.value)}
                  placeholder="Explain why this emergency override is necessary..."
                  className="w-full h-32 px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
              </div>

              <div className="bg-warning/10 border border-warning/20 rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    This action will be permanently logged with your admin credentials and cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  setShowConfirmModal(false);
                  setOverrideReason('');
                }}
                variant="outline"
                fullWidth
              >
                Cancel
              </Button>
              <Button
                onClick={handleOverrideAction}
                loading={isProcessing}
                fullWidth
                className="bg-error hover:bg-error/90 text-white"
              >
                Execute Override
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmergencyOverridePanel;